import { Injectable } from '@angular/core';
import { loadCssOnce } from '../helpers/functions/css-loader';
import { MapLibre } from './jawgmaps.service';

// 1. List of available libraries
export type LazyLoadedLibraries = 'maplibre-gl';

// 2. Map between library name and its import type
export interface LazyLibraryMap {
  'maplibre-gl': MapLibre;
}

// 3. Static mapping of modules to load
// see webpack doc here: https://webpack.js.org/api/module-methods/
const availableModules: { [P in LazyLoadedLibraries]: () => Promise<LazyLibraryMap[P]> } = {
  'maplibre-gl': () =>
    import(
      /* webpackChunkName: "maplibre-gl" */
      'maplibre-gl'
    ),
} as const;

/**
 * Generic service to lazy-load JavaScript libraries on demand
 */
@Injectable({ providedIn: 'root' })
export class LazyLoaderService {
  private readonly loadedLibraries = new Map<LazyLoadedLibraries, Promise<LazyLibraryMap[keyof LazyLibraryMap]>>();

  /**
   * Loads a library dynamically if not already loaded
   * @param path The library name (example: 'maplibre-gl')
   * @param cssHref The path to the CSS file (example: 'assets/maplibre/maplibre-gl.css')
   * @returns A promise resolving to the loaded module
   */
  async loadLibrary<T extends LazyLoadedLibraries>(path: T, cssHref?: string): Promise<LazyLibraryMap[T]> {
    if (this.loadedLibraries.has(path)) {
      return this.loadedLibraries.get(path);
    }

    if (!!cssHref) {
      loadCssOnce(cssHref);
    }

    const loadingPromise = availableModules[path]().then((module) => {
      /**
       * Handle ESM/CommonJS interop: The new Angular builder (esbuild/Vite)
       * often wraps dynamic imports in a '.default' property.
       */
      return (module as any).default || module;
    });
    this.loadedLibraries.set(path, loadingPromise);

    return loadingPromise;
  }
}
